import React, { useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { toast } from 'react-toastify';
import i18next from 'i18next';
import { useMutation, useQuery } from 'react-query';
import { fetchApi } from 'src/api/api';
import { courseResponse, relatedItemRequest } from 'src/models/course';
import { fetchCourseRelatedItems, fetchCourses } from 'src/services/courses';
import { defaultTranslation } from 'src/utils/helpers/default-translation';
import { fetchShopItems } from 'src/services/shop-items';
import { fetchBlogs } from 'src/services/blogs';
import { shopItemResponse } from 'src/models/shop-item';
import { BlogResponse } from 'src/models/blog';
import { ErrorFallBackNotFound } from 'src/components/error-not-found-fallback';
import AddRelatedItem from '../create-course/add-related-course-items/add-related-item';

type Props = {
  id: number;
  handleNext: Function;
};
const inputStyle = {
  width: { xs: '100%', sm: '92%' },
  marginBottom: '30px'
};

const EditRelatedCourseItems = (props: Props) => {
  const [modifiedCourses, setModifiedCourses] = useState<
    { id: number; type: string; name: string }[]
  >([]);

  const [modifiedShopItems, setModifiedShopItems] = useState<
    { id: number; type: string; name: string }[]
  >([]);

  const [modifiedBlogs, setModifiedBlogs] = useState<
    { id: number; type: string; name: string }[]
  >([]);

  const { isLoading: loadingRequest, mutateAsync } = useMutation(
    (data: any) =>
      fetchApi(
        `/course/relatedItem/createRelatableItem/${props.id}`,
        'POST',
        data
      ),
    {
      onError: (error: any) => {
        toast.error(
          i18next.language === 'en'
            ? 'Something went wrong, please try again later.'
            : 'حدث خطأ ما، يرجى المحاولة مرة أخرى لاحقًا.'
        );
      }
    }
  );

  const {
    handleSubmit,
    control,
    setValue,
    watch,
    formState: { errors }
  } = useForm<relatedItemRequest[]>({});

  const onSubmit: SubmitHandler<relatedItemRequest[]> = async (data) => {
    const relatable = Object.values(data).flat();
    try {
      mutateAsync({
        relatable
      }).then((response) => {
        if (
          response.status === 200 ||
          response.status === 201 ||
          response.status === 202
        ) {
          toast.success(
            i18next.language === 'en'
              ? 'Related Items Added successfully'
              : 'تم إضافة العناصر المتعلقة بالدورة بنجاح'
          );
          props.handleNext();
        }
      });
    } catch (error) {
      console.log(error);
    }
  };

  const {
    data: courses,
    isLoading,
    isFetching: f1,
    isError
  } = useQuery(['get-courses'], () => fetchCourses(null), {
    onSuccess: (data) => {
      // Modify the data to the desired format
      const modifiedData = data.map((course) => ({
        id: course.id,
        type: 'course',
        name: defaultTranslation(course, 'name')
      }));

      setModifiedCourses(modifiedData);
    }
  });

  const {
    data: shopItems,
    isLoading: l2,
    isFetching: f2,
    isError: e2
  } = useQuery(['get-shop-items'], () => fetchShopItems(), {
    onSuccess: (data) => {
      // Modify the data to the desired format
      const modifiedData = data.map((item) => ({
        id: item.id,
        type: 'shopitem',
        name: defaultTranslation(item, 'name')
      }));

      setModifiedShopItems(modifiedData);
    }
  });

  const {
    data: blogs,
    isLoading: l3,
    isFetching: f3,
    isError: e3
  } = useQuery(['get-blogs'], () => fetchBlogs(), {
    onSuccess: (data) => {
      // Modify the data to the desired format
      const modifiedData = data.map((item) => ({
        id: item.id,
        type: 'blog',
        name: defaultTranslation(item, 'name')
      }));

      setModifiedBlogs(modifiedData);
    }
  });

  const {
    data: oldRelated,
    isLoading: loadingResponse,
    isFetching: f4,
    isError: errorResponse
  } = useQuery(
    ['get-related', props.id],
    () => fetchCourseRelatedItems(props.id),
    {
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      onSuccess: (data) => {
        const tempCourses: relatedItemRequest[] = [];
        const tempBlogs: relatedItemRequest[] = [];
        const tempShopItems: relatedItemRequest[] = [];

        if (data.shop_items) {
          data.shop_items.forEach((item: shopItemResponse) => {
            tempShopItems.push({
              id: item.id,
              type: 'shopitem',
              name: defaultTranslation(item, 'name')
            });
          });
        }

        if (data.courses) {
          data.courses.forEach((course: courseResponse) => {
            tempCourses.push({
              id: course.id,
              type: 'course',
              name: defaultTranslation(course, 'name')
            });
          });
        }

        if (data.blogs) {
          data.blogs.forEach((blog: BlogResponse) => {
            tempBlogs.push({
              id: blog.id,
              type: 'blog',
              name: defaultTranslation(blog, 'name')
            });
          });
        }

        //@ts-ignore
        setValue('course', tempCourses);
        //@ts-ignore
        setValue('blog', tempBlogs);
        //@ts-ignore
        setValue('shopitem', tempShopItems);
      }
    }
  );

  if (errorResponse) return <ErrorFallBackNotFound />;
  if (
    isLoading ||
    l2 ||
    l3 ||
    loadingRequest ||
    loadingResponse ||
    f1 ||
    f2 ||
    f3 ||
    f4
  )
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '60vh'
        }}
      >
        <CircularProgress />
      </Box>
    );
  return (
    <form
      className="d-flex flex-wrap container"
      style={{
        width: '100%',
        gap: '2%',
        padding: '20px 0 50px 0',
        justifyContent: 'center'
      }}
      onSubmit={handleSubmit(onSubmit)}
    >
      <Box sx={inputStyle}>
        <AddRelatedItem
          control={control}
          tags={modifiedCourses!}
          name="course"
          label="Add Related Courses"
          watch={watch}
          setValue={setValue}
          oldValue={oldRelated?.courses}
          courseId={props.id}
        />
      </Box>

      <Box sx={inputStyle}>
        <AddRelatedItem
          control={control}
          tags={modifiedShopItems!}
          name="shopitem"
          label="Add Related shop Items"
          watch={watch}
          setValue={setValue}
          oldValue={oldRelated?.shop_items}
          courseId={props.id}
        />
      </Box>

      <Box sx={inputStyle}>
        <AddRelatedItem
          control={control}
          tags={modifiedBlogs!}
          name="blog"
          label="Add Related Blogs"
          watch={watch}
          setValue={setValue}
          oldValue={oldRelated?.blogs}
          courseId={props.id}
        />
      </Box>

      <div className="col-12 d-flex justify-content-center pt-4">
        <Button
          variant="contained"
          color="primary"
          className="mb-2 btn-hover-secondary btn-width-100"
          type="submit"
          sx={{ width: { xs: '100%', sm: '92%', lg: '30%' } }}
        >
          <Typography fontSize="large" color="#fff">
            submit
          </Typography>
        </Button>
      </div>
    </form>
  );
};

export default EditRelatedCourseItems;
